import { videoSource, type Media } from '@survey/core';
import { Pause, Play, RotateCcw, Square, Volume2, VolumeX } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { mediaUrl } from '../api/client';
import { useI18n } from '../i18n';

/** Image or video shown with a question. Uploaded and direct videos get the
 *  native player plus play/pause, rewind, stop and mute buttons; YouTube and
 *  Vimeo links are embedded with their own controls. */
export function QuestionMedia({ media }: { media: Media }) {
  if (media.type === 'image') {
    return (
      <figure className="q-media">
        <img src={mediaUrl(media.url)} alt={media.alt ?? ''} loading="lazy" />
        {media.alt && <figcaption>{media.alt}</figcaption>}
      </figure>
    );
  }
  const src = videoSource(media.url);
  return (
    <figure className="q-media">
      {src.kind === 'file' ? <FileVideo media={media} /> : <EmbedVideo media={media} kind={src.kind} id={src.id} />}
      {media.alt && <figcaption>{media.alt}</figcaption>}
    </figure>
  );
}

function FileVideo({ media }: { media: Media }) {
  const { t } = useI18n();
  const ref = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(!!media.muted);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    v.muted = !!media.muted;
    setMuted(!!media.muted);
    if (media.autoplay) {
      // Browsers may refuse unmuted autoplay; retry muted so the video still starts.
      v.play().catch(() => {
        v.muted = true;
        setMuted(true);
        v.play().catch(() => undefined);
      });
    }
  }, [media.url, media.autoplay, media.muted]);

  const v = () => ref.current;
  const toggle = () => {
    const el = v();
    if (!el) return;
    if (el.paused) void el.play().catch(() => undefined);
    else el.pause();
  };
  const rewind = () => {
    const el = v();
    if (el) el.currentTime = Math.max(0, el.currentTime - 10);
  };
  const stop = () => {
    const el = v();
    if (!el) return;
    el.pause();
    el.currentTime = 0;
  };
  const toggleMute = () => {
    const el = v();
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
  };

  return (
    <>
      <video
        ref={ref}
        src={mediaUrl(media.url)}
        controls
        playsInline
        preload="metadata"
        muted={muted}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onVolumeChange={(e) => setMuted(e.currentTarget.muted)}
      />
      <div className="q-media-controls">
        <button type="button" className="btn sm" onClick={toggle}>
          {playing ? <Pause /> : <Play />} {playing ? t('media.pause') : t('media.play')}
        </button>
        <button type="button" className="btn sm" onClick={rewind}>
          <RotateCcw /> {t('media.rewind')}
        </button>
        <button type="button" className="btn sm" onClick={stop}>
          <Square /> {t('media.stop')}
        </button>
        <button type="button" className="btn sm" onClick={toggleMute} aria-pressed={muted}>
          {muted ? <VolumeX /> : <Volume2 />} {muted ? t('media.unmute') : t('media.mute')}
        </button>
      </div>
    </>
  );
}

export function embedUrl(kind: 'youtube' | 'vimeo', id: string, media: Pick<Media, 'autoplay' | 'muted'>) {
  const autoplay = media.autoplay ? '1' : '0';
  const mute = media.muted ? '1' : '0';
  return kind === 'youtube'
    ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=${autoplay}&mute=${mute}&playsinline=1&rel=0`
    : `https://player.vimeo.com/video/${encodeURIComponent(id)}?autoplay=${autoplay}&muted=${mute}&playsinline=1`;
}

function EmbedVideo({ media, kind, id }: { media: Media; kind: 'youtube' | 'vimeo'; id: string }) {
  const { t } = useI18n();
  // Remounting the frame is the simplest reliable "stop" for third-party players.
  const [round, setRound] = useState(0);
  return (
    <>
      <div className="q-media-frame">
        <iframe
          key={round}
          src={embedUrl(kind, id, round === 0 ? media : { muted: media.muted })}
          title={media.alt || t('media.video')}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
      <div className="q-media-controls">
        <button type="button" className="btn sm" onClick={() => setRound((r) => r + 1)}>
          <Square /> {t('media.stop')}
        </button>
      </div>
    </>
  );
}
